import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui-components/ui/card';
import { Switch } from '../../../ui-components/ui/switch';
import { Label } from '../../../ui-components/ui/label';
import { Badge } from '../../../ui-components/ui/badge';
import { 
  TrendingUp, 
  TrendingDown, 
  Euro, 
  Users,
  Calculator
} from 'lucide-react';
import { cn } from '../../../lib/utils/utils';

interface LeadCostsData {
  totalLeads: number;
  ownLeads: number;
  purchasedLeads: number;
  totalCost: number;
  costPerLead: number;
  conversionRate: number;
  costPerConversion: number;
}

const LeadCostsTile: React.FC = () => {
  const [showOwnLeads, setShowOwnLeads] = useState(false);
  const [leadCosts] = useState<LeadCostsData>({
    totalLeads: 156,
    ownLeads: 42,
    purchasedLeads: 114,
    totalCost: 5700,
    costPerLead: 50, // Only for purchased leads
    conversionRate: 12.3,
    costPerConversion: 463.41,
  });

  const displayedData = showOwnLeads 
    ? {
        leads: leadCosts.totalLeads,
        cost: leadCosts.totalCost,
        avgCost: leadCosts.totalCost / leadCosts.totalLeads,
      }
    : {
        leads: leadCosts.purchasedLeads,
        cost: leadCosts.totalCost,
        avgCost: leadCosts.costPerLead,
      };

  const costChange = showOwnLeads ? -15.2 : 8.3; // Example percentages

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Lead-Kosten</CardTitle>
            <CardDescription>
              {showOwnLeads ? 'Alle Leads inkl. Eigenleads' : 'Nur gekaufte Leads'}
            </CardDescription>
          </div>
          <div className="flex items-center space-x-2">
            <Label htmlFor="own-leads" className="text-sm">
              Eigenleads einbeziehen
            </Label>
            <Switch
              id="own-leads"
              checked={showOwnLeads}
              onCheckedChange={setShowOwnLeads}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-4">
          {/* Total Leads */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">Leads</p>
              <Users className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold">{displayedData.leads}</p>
            {showOwnLeads && (
              <div className="flex gap-2">
                <Badge variant="secondary" className="text-xs">
                  {leadCosts.ownLeads} Eigen
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {leadCosts.purchasedLeads} Gekauft
                </Badge>
              </div>
            )}
          </div>

          {/* Total Cost */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">Gesamtkosten</p>
              <Euro className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold">€{displayedData.cost.toLocaleString('de-DE')}</p>
            <div className="flex items-center gap-1">
              {costChange > 0 ? (
                <TrendingUp className="h-4 w-4 text-red-500" />
              ) : (
                <TrendingDown className="h-4 w-4 text-green-500" />
              )}
              <span className={cn(
                "text-sm font-medium",
                costChange > 0 ? "text-red-500" : "text-green-500"
              )}>
                {Math.abs(costChange)}%
              </span>
              <span className="text-xs text-muted-foreground">vs. letzte Woche</span>
            </div>
          </div>

          {/* Cost per Lead */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">Kosten/Lead</p>
              <Calculator className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold">€{displayedData.avgCost.toFixed(2)}</p>
            {showOwnLeads && (
              <p className="text-xs text-muted-foreground">
                Ø inkl. Eigenleads
              </p>
            )}
          </div>

          {/* Cost per Conversion */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">Kosten/Abschluss</p>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold">€{leadCosts.costPerConversion.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground">
              bei {leadCosts.conversionRate}% CR
            </p>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-4 pt-4 border-t">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              Lead-Budget diese Woche
            </span>
            <div className="flex items-center gap-2">
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-primary h-2 rounded-full" 
                  style={{ width: '68%' }}
                />
              </div>
              <span className="font-medium">€2,324 / €3,400</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default LeadCostsTile;
